import { Box, Stack, Button, styled, ButtonProps, IconButton, StackProps, IconButtonProps } from '@mui/material'

interface ContainerProps extends StackProps {
  disabled?: boolean
}

/** Tab Button Group Container */
export const StyledContainer = styled(Stack, { shouldForwardProp: prop => prop !== 'disabled' })<ContainerProps>(({ theme, disabled }) => {
  return {
    borderWidth: 1,
    borderStyle: 'solid',
    /* '!important' will override any backgroundColor passed through sx, ensuring 'disabled' take precedence over any sx styles */
    borderColor: disabled ? `${theme.palette.shades.shade03} !important` : theme.palette.grey.grey06,
    backgroundColor: disabled ? `${theme.palette.shades.shade03} !important` : theme.palette.background.paper,
    borderRadius: theme.shape.customBorderRadius.tabButtonGroup,
    position: 'relative',
    overflow: 'hidden',
    minWidth: '60px',
  }
})

/** Sliding background (highlight) */
export const SlidingBackground = styled(Box)<{ selectedindex: number; childcount: number; disabled: boolean }>(
  ({ theme, selectedindex, childcount, disabled }) => ({
    position: 'absolute',
    top: 0,
    translate: `${100 * selectedindex}%`,
    /* exactly one button width */
    width: `${100 / childcount}%`,
    height: '100%',
    backgroundColor: disabled ? theme.palette.primary.light : theme.palette.primary.main,
    borderRadius: theme.shape.customBorderRadius.tabButton,
    transition: theme.transitions.create(['translate'], {
      duration: theme.transitions.duration.short,
      easing: theme.transitions.easing.easeInOut,
    }),
    /* Always below button */
    zIndex: 0,
  })
)

/** Tab Button */
interface TabButtonProps extends ButtonProps {
  isActive?: boolean
  isDisabled?: boolean
  /* tooltip text shown on hover, most useful in combination with 'isDisabled' */
  tooltip?: string
}

export const TabButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'isActive',
})<TabButtonProps>(({ theme, isDisabled, isActive }) => ({
  flex: 1,
  /* Always above highlight */
  zIndex: 1,
  borderRadius: theme.shape.customBorderRadius.tabButton,

  ...theme.typography.button,
  textTransform: 'none',
  color: isActive ? theme.palette.common.white : theme.palette.text.primary,
  backgroundColor: 'transparent',
  minHeight: 32 /** override muiTheme/Button height per design of core TabButtonGroup component in Figma */,
  maxHeight: 32 /** override muiTheme/Button height per design of core TabButtonGroup component in Figma */,

  '& .tab-label': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'block',
    width: '100%',
    textAlign: 'left',
  },

  '&:hover': {
    backgroundColor: isActive ? theme.palette.primary.dark : theme.palette.primary.light,
  },

  ...(isDisabled && {
    color: theme.palette.text.disabled,
    pointerEvents: 'none',
    borderColor: isActive ? `${theme.palette.shades.shade03} !important` : theme.palette.grey.grey06,
    backgroundColor: isActive ? `${theme.palette.shades.shade03} !important` : theme.palette.background.paper,
  }),
}))

TabButton.displayName = 'TabButton'

/** Icon Button */
interface TabIconButtonProps extends IconButtonProps {
  isActive?: boolean
}

export const TabIconButton = styled(IconButton, {
  shouldForwardProp: prop => prop !== 'isActive',
})<TabIconButtonProps>(({ theme, isActive }) => ({
  flex: 1,
  /* Always above highlight */
  zIndex: 1,
  borderRadius: theme.shape.customBorderRadius.tabButton,

  padding: '2px',

  ...theme.typography.h3,
  textTransform: 'none',
  color: isActive ? theme.palette.common.white : theme.palette.text.primary,
  backgroundColor: 'transparent',

  /* Style the SVG icon inside the button */
  '& > svg': {
    width: '20px',
    height: '20px',
  },

  '&:hover': {
    backgroundColor: isActive ? theme.palette.primary.dark : theme.palette.primary.light,
  },
}))

TabIconButton.displayName = 'TabIconButton'
